import { useState } from "react";
import { Button } from "antd";
import { BaseItem as ZhBaseItem, Dialog as ZhDialog, ZhMap } from "zhihao-ui-react";

import { areaLib } from "@/utils/areaLib";

export interface ProductDetailLocationData {
    provinceCode?: string;
    cityCode?: string;
    regionCode?: string;
    address?: string;
    geo?: { lng?: number | string; lat?: number | string };
}

interface ProductDetailMapDialogProps {
    location: ProductDetailLocationData;
    span?: number;
    labelWidth?: number;
    /** 明细子页已经自行渲染 label 时，只输出地址入口和地图弹窗。 */
    inline?: boolean;
}

/** 将 Vue geo 对象转换成 zhihao 地图需要的经纬度中心点。 */
function parseMapCenter(location: ProductDetailLocationData): [number, number] | undefined {
    const lng = Number(location.geo?.lng);
    const lat = Number(location.geo?.lat);
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : undefined;
}

/** 商品详情提货地点，复用 Vue 的地址字段并提供只读地图查看入口。 */
export function ProductDetailMapDialog({
    location,
    span = 3,
    labelWidth = 112,
    inline = false
}: ProductDetailMapDialogProps) {
    const [open, setOpen] = useState(false);
    const center = parseMapCenter(location);
    const areaCodes = [location.provinceCode, location.cityCode, location.regionCode].filter(Boolean).join("/");
    const areaName = areaLib.getFullAreaName(location.provinceCode, location.cityCode, location.regionCode);
    // 地区库偶尔会返回包含详细地址的全称，沿用 Vue 的展示规则避免重复拼接。
    const locationText =
        areaName && location.address && areaName.endsWith(location.address)
            ? location.address
            : [areaName, location.address].filter(Boolean).join(" ") || "--";
    const canViewMap = Boolean(areaCodes && center);

    const locationEntry = canViewMap ? (
        <button
            aria-label="查看提货地点地图"
            className="product-detail-map-trigger inline-flex items-center text-left"
            type="button"
            onClick={() => setOpen(true)}
        >
            <span>
                {!inline && <span className="product-detail-compat-location">{areaCodes} </span>}
                {locationText}
            </span>
            <i aria-hidden="true" className="iconfont icon-map lngLat ml-8" />
        </button>
    ) : (
        <span>
            <span className="product-detail-compat-location">{areaCodes} </span>
            {locationText}
        </span>
    );

    return (
        <>
            {inline ? (
                locationEntry
            ) : (
                <ZhBaseItem colon={false} label="提货地点" labelWidth={labelWidth} span={span}>
                    {locationEntry}
                </ZhBaseItem>
            )}
            <ZhDialog
                open={open}
                title="查看位置"
                width="920px"
                closeOnClickModal={false}
                destroyOnClose
                showClose
                onCancel={() => setOpen(false)}
                footer={<Button onClick={() => setOpen(false)}>取消</Button>}
            >
                <section
                    aria-label="商品提货地点地图"
                    className="product-detail-map-viewport relative h-[468px] w-full"
                >
                    <ZhMap center={center} className="h-full w-full" layerType="satellite" showScale />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="product-detail-map-center flex flex-col items-center">
                            <span aria-label="地图位置标记" className="product-detail-map-marker" />
                            <span aria-label="地图地址" className="product-detail-map-address">
                                {locationText}
                            </span>
                        </div>
                    </div>
                </section>
            </ZhDialog>
        </>
    );
}

export default ProductDetailMapDialog;
